// RPTEXPORT1-F2 (docs/build-briefs/RPTEXPORT1-pdf-and-google-sheets-export.md; owner ruling 2026-10-20: "Don't store in cloud.
// The user will sign in to their Google account locally on their machine when they want to export to sheets."). The pure half of
// the Export to Google Sheets button: everything the BROWSER sends to the Sheets API, built from the report JSON the server
// answered (src/domain/googleSheets/sheetsPayload.ts). No DOM and no network, so a plain node:test file can import it.
//
//   * the header row is row 1, frozen and bold;
//   * a money column is a real number shown as dollars with the currency pattern, a percent a real percent, a count a number;
//   * the totals row, when the report has one, is the last row and bold;
//   * a text value is always sent as a string value, never as a formula, so a name that starts with "=" stays text.

export const SHEETS_API_URL = "https://sheets.googleapis.com/v4/spreadsheets";
/** Rows sent per batchUpdate request, to keep each request small. */
export const SHEETS_ROWS_PER_REQUEST = 2000;

export type SheetsCell = string | number | null;
export type SheetsColumnType = "text" | "money" | "percent" | "number" | "decimal" | "date" | string;

export interface SheetsPayloadColumn {
  label: string;
  type: SheetsColumnType;
  pattern?: string;
}
export interface SheetsPayloadTab {
  name: string;
  columns: SheetsPayloadColumn[];
  rows: SheetsCell[][];
  totals: SheetsCell[] | null;
}
export interface SheetsPayload {
  title: string;
  tabs: SheetsPayloadTab[];
  about: Array<[string, string]>;
}

export const SHEETS_MESSAGES = Object.freeze({
  creating: "Creating your Google Sheet.",
  opened: "Opened your Google Sheet in a new tab.",
  ready: "Your Google Sheet is ready.",
  createFailed: "Could not create the Google Sheet. Nothing was saved to your Google Drive.",
  writeFailed:
    "The Google Sheet was created but could not be filled in. Delete it from your Google Drive and try again.",
  unreadable: "Could not read this report for Google Sheets. Nothing was saved to your Google Drive.",
  optionsFailed: "Could not check whether Google Sheets is set up. Try again.",
});

function isCell(value: unknown): value is SheetsCell {
  return value === null || typeof value === "string" || (typeof value === "number" && Number.isFinite(value));
}

/** The report JSON the server answered, or null when it is not the shape the browser can write. */
export function parseSheetsPayload(body: unknown): SheetsPayload | null {
  const sheet = (body as { sheet?: unknown } | null)?.sheet as Record<string, unknown> | undefined;
  if (typeof sheet !== "object" || sheet === null) return null;
  const { title, tabs, about } = sheet;
  if (typeof title !== "string" || !Array.isArray(tabs) || tabs.length === 0 || !Array.isArray(about)) return null;
  for (const tab of tabs) {
    const t = tab as Record<string, unknown>;
    if (typeof t?.["name"] !== "string" || !Array.isArray(t["columns"]) || !Array.isArray(t["rows"])) return null;
    const width = (t["columns"] as unknown[]).length;
    for (const column of t["columns"] as Array<Record<string, unknown>>) {
      if (typeof column?.["label"] !== "string" || typeof column["type"] !== "string") return null;
    }
    for (const row of t["rows"] as unknown[]) {
      if (!Array.isArray(row) || row.length !== width || !row.every(isCell)) return null;
    }
    const totals = t["totals"];
    if (totals !== null && !(Array.isArray(totals) && totals.length === width && totals.every(isCell))) return null;
  }
  for (const line of about) {
    if (!Array.isArray(line) || line.length !== 2 || typeof line[0] !== "string" || typeof line[1] !== "string") return null;
  }
  return sheet as unknown as SheetsPayload;
}

interface GoogleCell {
  userEnteredValue?: { numberValue?: number; stringValue?: string };
  userEnteredFormat?: {
    numberFormat?: { type: "CURRENCY" | "PERCENT" | "NUMBER"; pattern: string };
    textFormat?: { bold: boolean };
    horizontalAlignment?: "RIGHT";
  };
}

function formatKind(type: string): "CURRENCY" | "PERCENT" | "NUMBER" | undefined {
  if (type === "money") return "CURRENCY";
  if (type === "percent") return "PERCENT";
  if (type === "number" || type === "decimal") return "NUMBER";
  return undefined;
}

export function cellFor(value: SheetsCell, column: SheetsPayloadColumn, bold: boolean): GoogleCell {
  const kind = formatKind(column.type);
  const format: NonNullable<GoogleCell["userEnteredFormat"]> = {};
  if (kind !== undefined && column.pattern !== undefined) {
    format.numberFormat = { type: kind, pattern: column.pattern };
    format.horizontalAlignment = "RIGHT";
  }
  if (bold) format.textFormat = { bold: true };
  const withFormat = (cell: GoogleCell): GoogleCell =>
    Object.keys(format).length === 0 ? cell : { ...cell, userEnteredFormat: format };
  if (value === null) return withFormat({});
  return withFormat(
    typeof value === "number"
      ? { userEnteredValue: { numberValue: value } }
      : { userEnteredValue: { stringValue: value } },
  );
}

function headerRow(tab: SheetsPayloadTab): GoogleCell[] {
  return tab.columns.map((column) => ({
    userEnteredValue: { stringValue: column.label },
    userEnteredFormat: {
      textFormat: { bold: true },
      ...(formatKind(column.type) === undefined ? {} : { horizontalAlignment: "RIGHT" as const }),
    },
  }));
}

/** Header, data rows, then the totals row when there is one. */
export function tabRows(tab: SheetsPayloadTab): GoogleCell[][] {
  const rows: GoogleCell[][] = [
    headerRow(tab),
    ...tab.rows.map((row) => tab.columns.map((column, i) => cellFor(row[i] ?? null, column, false))),
  ];
  if (tab.totals !== null) rows.push(tab.columns.map((column, i) => cellFor(tab.totals![i] ?? null, column, true)));
  return rows;
}

function uniqueAboutName(payload: SheetsPayload): string {
  const used = new Set(payload.tabs.map((tab) => tab.name.toLowerCase()));
  return used.has("about") ? "About this report" : "About";
}

/** The spreadsheets.create body: the title, one tab per report tab with the header row frozen, and an About tab. */
export function buildCreateBody(payload: SheetsPayload): unknown {
  return {
    properties: { title: payload.title },
    sheets: [
      ...payload.tabs.map((tab) => ({
        properties: {
          title: tab.name,
          gridProperties: {
            rowCount: Math.max(2, tab.rows.length + 2),
            columnCount: Math.max(1, tab.columns.length),
            frozenRowCount: 1,
          },
        },
      })),
      {
        properties: {
          title: uniqueAboutName(payload),
          gridProperties: { rowCount: Math.max(2, payload.about.length), columnCount: 2 },
        },
      },
    ],
  };
}

/** The batchUpdate requests that fill the created sheets, grouped so no single call carries tens of thousands of rows.
 * `sheetIds` lists the created tabs' ids in creation order (the report tabs, then About). */
export function buildBatchGroups(payload: SheetsPayload, sheetIds: number[]): unknown[][] {
  const requests: Array<{ request: unknown; rows: number }> = [];
  payload.tabs.forEach((tab, index) => {
    const sheetId = sheetIds[index]!;
    const rows = tabRows(tab);
    for (let start = 0; start < rows.length; start += SHEETS_ROWS_PER_REQUEST) {
      const slice = rows.slice(start, start + SHEETS_ROWS_PER_REQUEST);
      requests.push({
        rows: slice.length,
        request: {
          updateCells: {
            rows: slice.map((values) => ({ values })),
            fields: "userEnteredValue,userEnteredFormat(numberFormat,textFormat,horizontalAlignment)",
            start: { sheetId, rowIndex: start, columnIndex: 0 },
          },
        },
      });
    }
    requests.push({
      rows: 0,
      request: {
        autoResizeDimensions: {
          dimensions: { sheetId, dimension: "COLUMNS", startIndex: 0, endIndex: tab.columns.length },
        },
      },
    });
  });
  const aboutId = sheetIds[payload.tabs.length]!;
  requests.push({
    rows: payload.about.length,
    request: {
      updateCells: {
        rows: payload.about.map(([label, value]) => ({
          values: [
            { userEnteredValue: { stringValue: label }, userEnteredFormat: { textFormat: { bold: true } } },
            { userEnteredValue: { stringValue: value } },
          ],
        })),
        fields: "userEnteredValue,userEnteredFormat(textFormat)",
        start: { sheetId: aboutId, rowIndex: 0, columnIndex: 0 },
      },
    },
  });
  requests.push({
    rows: 0,
    request: {
      autoResizeDimensions: { dimensions: { sheetId: aboutId, dimension: "COLUMNS", startIndex: 0, endIndex: 2 } },
    },
  });
  const groups: unknown[][] = [];
  let group: unknown[] = [];
  let groupRows = 0;
  for (const { request, rows } of requests) {
    if (groupRows + rows > SHEETS_ROWS_PER_REQUEST * 2 && group.length > 0) {
      groups.push(group);
      group = [];
      groupRows = 0;
    }
    group.push(request);
    groupRows += rows;
  }
  if (group.length > 0) groups.push(group);
  return groups;
}

/** The created spreadsheet's id, address and tab ids, or null when Google's answer is not the shape expected. The address
 * must be Google's own sheets host over https; the tab count must match what was asked. */
export function parseCreatedSpreadsheet(
  body: unknown,
  expectedTabs: number,
): { spreadsheetId: string; url: string; sheetIds: number[] } | null {
  if (typeof body !== "object" || body === null) return null;
  const { spreadsheetId, spreadsheetUrl, sheets } = body as Record<string, unknown>;
  if (typeof spreadsheetId !== "string" || typeof spreadsheetUrl !== "string" || !Array.isArray(sheets)) return null;
  if (sheets.length !== expectedTabs) return null;
  const ids = sheets.map((s) => (s as { properties?: { sheetId?: unknown } })?.properties?.sheetId);
  if (!ids.every((id): id is number => typeof id === "number")) return null;
  try {
    const url = new URL(spreadsheetUrl);
    if (url.protocol !== "https:" || url.hostname !== "docs.google.com" || !url.pathname.startsWith("/spreadsheets/"))
      return null;
    return { spreadsheetId, url: url.toString(), sheetIds: ids };
  } catch {
    return null;
  }
}
